// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The canonical link, per route (M3-57).
//
// **A separate module from `sitemap.ts` for a mechanical reason, and it is worth stating.**
// `vite.config.ts` imports the sitemap at build time, and Vite's `configLoader: 'native'` — which
// is planned to become its default — resolves a config's imports with Node's own rules: an import
// without a file extension, or one that lands on a directory index, makes it fall back and print a
// warning on every `vite build`, `npm run dev` and test run. `sitemap.ts` therefore imports
// nothing, and the half that needs the route table lives here, where only the browser bundle reads
// it and Vite's own resolver is the one doing the work.
//
// The two halves are still one decision: `INDEXING` says which routes are offered to a crawler,
// the sitemap lists exactly those, and this file gives exactly those a canonical.

import { matchesRoute, servedRoutes } from '../client/routes'
import { isUnknownGuide } from '../client/machineGuides'
import { INDEXING, SITE_ORIGIN } from './sitemap'

/**
 * The address a crawler should treat as this page's own, or nothing when it should not treat it as
 * a page at all.
 *
 * **This is the half `index.html` cannot do.** Render answers every address with one file, so a
 * canonical link written into that file would name the front door on `/arcade`, on `/pricing` and
 * on every machine — an instruction to fold five real pages into one. Written per route, from the
 * pathname alone, it does the two jobs a canonical is actually for here: it says which of the
 * product's addresses this page *is*, and it drops the query string, which is what makes
 * `/play/neon-nova?join=…` and `/play/neon-nova` one page rather than an invite link's worth of
 * duplicates.
 *
 * A route the sitemap does not index gets nothing rather than a canonical pointing elsewhere.
 * `robots.txt` is what keeps a crawler off those; a canonical is a statement about a page a crawler
 * is welcome on.
 */
export function canonicalFor(pathname: string): string | undefined {
  let best: string | undefined
  for (const route of servedRoutes()) {
    if (!matchesRoute(route.path, pathname)) continue
    // Longest literal pattern wins, the same tie-break `titleFor` uses, so `/` does not claim
    // every address in the product.
    if (best === undefined || route.path.length > best.length) best = route.path
  }
  if (best === undefined || INDEXING[best]?.index !== true) return undefined
  // A guide's pattern matches any id and the roster is not in the route table (S3-10). An id with
  // no guide is a page that says "nothing here", and a canonical would claim it is one.
  if (isUnknownGuide(pathname)) return undefined
  // Trailing slashes are not part of any route pattern, so `/arcade/` and `/arcade` are one page.
  const path = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  return `${SITE_ORIGIN}${path}`
}
